import React from 'react';
import NotFound from '../../NotFound';
import TaskSearchItem from '../TaskSearchItem';
import { TaskSearchItemsLoadingMoreSkeleton } from '../TaskSearchItemSkeleton';
import { TASK_GROUP_CONFIG, TASK_GROUP_KEYS, TASK_PAST_GROUP_KEY } from '../../../helpers/constants';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';

const columnStylesByKey = {
  overdue: { background: '#FEF2F2' },
  today: { background: '#EFF6FF' }
};

const KanbanColumn = ({
  groupKey,
  tasks,
  onTaskDelete,
  onEditClick,
  onStatusUpdate,
  onBottomReached,
  loadingMore,
  highlightedTaskId,
  totalCount,
  showDescription = false
}) => {
  const config = TASK_GROUP_CONFIG[groupKey];
  const Icon = config.icon;

  const { scrollContainerRef } = useInfiniteScroll({
    onLoadMore: onBottomReached,
    hasMore: !!onBottomReached,
    loadingMore,
    items: tasks
  });

  return (
    <div
      className="w-100 rounded p-2"
      style={{ background: '#F7F7F7', minWidth: 0, maxWidth: '100%', overflow: 'hidden', ...(columnStylesByKey[groupKey] || {}) }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2 px-1">
        <span className="d-flex align-items-center smallFont font-weight-semibold" style={{ gap: 4, color: config.labelColor }}>
          <Icon size={13} color={config.iconColor} />
          {config.label}
        </span>
        <span className="xtinyFont rounded-pill px-2 py-1" style={{ background: config.countBg, color: config.countColor }}>
          {totalCount || tasks?.length}
        </span>
      </div>

      <div
        ref={scrollContainerRef}
        className="hide-scrollbar"
        style={{ height: 'calc(100vh - 350px)', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {tasks.map(task => (
          <div key={task?._id} className="mb-2" style={{ minWidth: 0, maxWidth: '100%' }}>
            <TaskSearchItem
              task={task}
              onTaskDelete={onTaskDelete}
              onEditClick={onEditClick}
              onStatusUpdate={onStatusUpdate}
              isHighlighted={highlightedTaskId === task._id}
              isKanbanView={true}
              showDescription={showDescription}
            />
          </div>
        ))}
        {loadingMore && (
          <TaskSearchItemsLoadingMoreSkeleton />
        )}
      </div>
    </div>
  );
};

const TasksKanbanView = ({
  taskBuckets = {},
  laterHasMore,
  pastHasMore,
  laterLoadingMore,
  pastLoadingMore,
  onLoadMoreLater,
  onLoadMorePast,
  onTaskDelete,
  onEditClick,
  onStatusUpdate,
  statusTab,
  highlightedTaskId,
  laterTotal = 0,
  pastTotal = 0,
  showDescription = false
}) => {
  const totalCount =
    TASK_GROUP_KEYS.reduce((sum, key) => sum + (taskBuckets[key]?.length || 0), 0) +
    (taskBuckets[TASK_PAST_GROUP_KEY]?.length || 0);

  if (totalCount === 0) {
    return <NotFound text="No tasks found" />;
  }

  const columnKeys = statusTab !== 'Incomplete' ? [...TASK_GROUP_KEYS, TASK_PAST_GROUP_KEY] : TASK_GROUP_KEYS;

  const getColumnScrollProps = groupKey => {
    if (groupKey === 'later' && laterHasMore) {
      return {
        onBottomReached: () => {
          if (!laterLoadingMore) onLoadMoreLater();
        },
        loadingMore: laterLoadingMore
      };
    }
    if (groupKey === TASK_PAST_GROUP_KEY && pastHasMore) {
      return {
        onBottomReached: () => {
          if (!pastLoadingMore) onLoadMorePast();
        },
        loadingMore: pastLoadingMore
      };
    }
    return {};
  };

  return (
    <div className="scrollbar-thin mt-3 pb-1" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      <div className="d-flex flex-nowrap" style={{ gap: 16, minWidth: 'min-content' }}>
        {columnKeys.map(groupKey => (
          <div
            key={groupKey}
            className="flex-shrink-0 d-flex"
            style={{ width: 300 }}
          >
            <KanbanColumn
              groupKey={groupKey}
              tasks={taskBuckets[groupKey] || []}
              onTaskDelete={onTaskDelete}
              onEditClick={onEditClick}
              onStatusUpdate={onStatusUpdate}
              highlightedTaskId={highlightedTaskId}
              totalCount={groupKey === 'later' ? laterTotal : groupKey === 'pastTasks' ? pastTotal : undefined}
              showDescription={showDescription}
              {...getColumnScrollProps(groupKey)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TasksKanbanView;
